import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

function ProgramariNew() {
    const navigate = useNavigate();
    // State for patient information (bare minimum fields)
    const [patientData, setPatientData] = useState({
        nume: '',
        prenume: '',
        email: '',
        telefon: ''
    });

    // State for appointment details
    const [appointmentData, setAppointmentData] = useState({
        id_specialitate: '',
        id_medic: '',
        data: '',
        ora: '',
        observatii: ''
    });

    // State for dropdown data
    const [specialties, setSpecialties] = useState([]);
    const [doctors, setDoctors] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [slotsLoading, setSlotsLoading] = useState(false);
    const [slotsLoaded, setSlotsLoaded] = useState(false);

    // UI state
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [marketingConsent, setMarketingConsent] = useState(false);

    // Load specialties on component mount
    useEffect(() => {
        loadSpecialties();
    }, []);

    // Load doctors when specialty changes
    useEffect(() => {
        if (appointmentData.id_specialitate) {
            loadDoctorsBySpecialty(appointmentData.id_specialitate);
        } else {
            setDoctors([]);
        }
    }, [appointmentData.id_specialitate]);

    // Helper function to generate time slots within a time range
    const generateTimeSlots = useCallback((startTime, endTime, slotDurationMinutes = 30) => {
        const slots = [];
        const start = new Date(`1970-01-01T${startTime}:00`);
        const end = new Date(`1970-01-01T${endTime}:00`);

        for (let time = new Date(start); time < end; time.setMinutes(time.getMinutes() + slotDurationMinutes)) {
            const timeString = time.toTimeString().substring(0, 5); // HH:MM format
            slots.push({
                value: timeString,
                label: timeString
            });
        }

        return slots;
    }, []);

    // Function to load available times for a specific date, checking for existing appointments
    const loadAvailableTimesForDate = useCallback(async (selectedDate, doctorId) => {
        try {
            // Find the schedule data for this date
            const selectedDateData = availableSlots.find(slot => slot.zi.split('T')[0] === selectedDate);
            if (!selectedDateData) {
                console.log('No date data found for:', selectedDate);
                setAvailableTimes([]);
                return;
            }

            // Generate all possible time slots from schedule
            const allTimeSlots = [];
            selectedDateData.intervale.forEach(interval => {
                // Extract start and end times
                let startTime = '00:00';
                let endTime = '00:00';

                if (typeof interval.start === 'string') {
                    const timeMatches = interval.start.match(/\d{1,2}:\d{2}:\d{2}/g);
                    if (timeMatches && timeMatches.length > 0) {
                        startTime = timeMatches[timeMatches.length - 1];
                    }
                    startTime = startTime.substring(0, 5); // HH:MM format
                }

                if (typeof interval.end === 'string') {
                    const timeMatches = interval.end.match(/\d{1,2}:\d{2}:\d{2}/g);
                    if (timeMatches && timeMatches.length > 0) {
                        endTime = timeMatches[timeMatches.length - 1];
                    }
                    endTime = endTime.substring(0, 5); // HH:MM format
                }

                // Generate time slots for this interval
                const intervalSlots = generateTimeSlots(startTime, endTime);
                allTimeSlots.push(...intervalSlots);
            });

            // Remove duplicates and sort
            const uniqueTimeSlots = allTimeSlots.filter((slot, index, self) =>
                index === self.findIndex(s => s.value === slot.value)
            ).sort((a, b) => a.value.localeCompare(b.value));

            // Now fetch existing appointments for this doctor and date
            // Convert date to Unix timestamps for start and end of day (UTC to match database)
            const selectedDateObj = new Date(selectedDate + 'T00:00:00Z'); // Create in UTC
            const startTimestamp = Math.floor(selectedDateObj.getTime() / 1000);
            const endTimestamp = startTimestamp + (24 * 60 * 60) - 1; // 23:59:59 of same day

            const appointmentsResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/v1/public/programari/list?start=${startTimestamp}&end=${endTimestamp}&id_medic=${doctorId}&include_canceled=1`,
                {
                    headers: {
                        'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (appointmentsResponse.ok) {
                const appointmentsData = await appointmentsResponse.json();
                const appointments = appointmentsData.items || [];

                // Filter out time slots that conflict with existing appointments
                const availableTimeSlots = uniqueTimeSlots.filter(slot => {
                    const slotTime = slot.value; // HH:MM format

                    // Check if this slot conflicts with any existing appointment
                    const hasConflict = appointments.some(appointment => {
                        // Extract time from appointment start (assuming format: "2026-02-10 09:30:00")
                        const appointmentStart = appointment.start || '';

                        const timeMatch = appointmentStart.match(/(\d{2}:\d{2}):\d{2}$/);
                        if (timeMatch) {
                            const appointmentTime = timeMatch[1]; // HH:MM format
                            return appointmentTime === slotTime;
                        }
                        return false;
                    });

                    return !hasConflict;
                });

                setAvailableTimes(availableTimeSlots);
            } else {
                setAvailableTimes(uniqueTimeSlots);
            }

        } catch (error) {
            console.error('Error loading available times for date:', error);
            // Fallback to showing all possible slots
            const selectedDateData = availableSlots.find(slot => slot.zi.split('T')[0] === selectedDate);
            if (selectedDateData) {
                const allTimeSlots = [];
                selectedDateData.intervale.forEach(interval => {
                    let startTime = '00:00';
                    let endTime = '00:00';

                    if (typeof interval.start === 'string') {
                        const timeMatches = interval.start.match(/\d{1,2}:\d{2}:\d{2}/g);
                        if (timeMatches && timeMatches.length > 0) {
                            startTime = timeMatches[timeMatches.length - 1];
                        }
                        startTime = startTime.substring(0, 5);
                    }

                    if (typeof interval.end === 'string') {
                        const timeMatches = interval.end.match(/\d{1,2}:\d{2}:\d{2}/g);
                        if (timeMatches && timeMatches.length > 0) {
                            endTime = timeMatches[timeMatches.length - 1];
                        }
                        endTime = endTime.substring(0, 5);
                    }

                    const intervalSlots = generateTimeSlots(startTime, endTime);
                    allTimeSlots.push(...intervalSlots);
                });

                const uniqueTimeSlots = allTimeSlots.filter((slot, index, self) =>
                    index === self.findIndex(s => s.value === slot.value)
                ).sort((a, b) => a.value.localeCompare(b.value));

                setAvailableTimes(uniqueTimeSlots);
            } else {
                setAvailableTimes([]);
            }
        }
    }, [availableSlots, generateTimeSlots]);

    // Load available slots when doctor changes
    useEffect(() => {
        if (appointmentData.id_medic) {
            loadAvailableSlots(appointmentData.id_medic);
        } else {
            setAvailableSlots([]);
            setAvailableDates([]);
            setAvailableTimes([]);
            setSlotsLoaded(false);
        }
    }, [appointmentData.id_medic]);

    // Load available times when date changes (now includes appointment checking)
    useEffect(() => {
        if (appointmentData.data && availableSlots.length > 0) {
            loadAvailableTimesForDate(appointmentData.data, appointmentData.id_medic);
        } else {
            setAvailableTimes([]);
        }
    }, [appointmentData.data, availableSlots, appointmentData.id_medic, loadAvailableTimesForDate]);

    const loadSpecialties = async () => {
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/v1/public/specialitati/list`;

            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
            }

            const data = await response.json();
            setSpecialties(data.items || []);
        } catch (err) {
            console.error('Error loading specialties:', err);
            setError(`Eroare la încărcarea specialităților: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadDoctorsBySpecialty = async (specialtyId) => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/medici/list?id_specialitate=${specialtyId}`, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setDoctors(data.items || []);
        } catch (err) {
            console.error('Error loading doctors:', err);
            setError('Eroare la încărcarea medicilor');
        } finally {
            setLoading(false);
        }
    };

    const loadAvailableSlots = async (doctorId) => {
        try {
            setSlotsLoading(true);
            setSlotsLoaded(false);
            const response = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/medici/${doctorId}/orar`, {
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const slots = data.orar || [];
            setAvailableSlots(slots);

            // Extract unique dates from available slots with time ranges
            const dates = slots.map(slot => {
                // Extract just the date part from zi (remove time and timezone)
                const dateOnly = slot.zi.split('T')[0];

                // Get the time range for this date
                const timeRange = slot.intervale.map(interval => {
                    // Extract start and end times
                    let startTime = '00:00';
                    let endTime = '00:00';

                    if (typeof interval.start === 'string') {
                        const timeMatches = interval.start.match(/\d{1,2}:\d{2}:\d{2}/g);
                        if (timeMatches && timeMatches.length > 0) {
                            startTime = timeMatches[timeMatches.length - 1];
                        }
                        startTime = startTime.substring(0, 5); // HH:MM format
                    }

                    if (typeof interval.end === 'string') {
                        const timeMatches = interval.end.match(/\d{1,2}:\d{2}:\d{2}/g);
                        if (timeMatches && timeMatches.length > 0) {
                            endTime = timeMatches[timeMatches.length - 1];
                        }
                        endTime = endTime.substring(0, 5); // HH:MM format
                    }

                    return `${startTime} - ${endTime}`;
                }).join(', '); // Join multiple ranges with comma

                return {
                    value: dateOnly, // Use date-only format
                    label: `${new Date(dateOnly).toLocaleDateString('ro-RO', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}, ora ${timeRange}`,
                    timeRanges: slot.intervale // Store the intervals for time slot generation
                };
            });
            setAvailableDates(dates);
            setSlotsLoaded(true);
        } catch (err) {
            console.error('Error loading available slots:', err);
            setError('Eroare la încărcarea programului medicului');
            setSlotsLoaded(true); // Still mark as loaded even on error
        } finally {
            setSlotsLoading(false);
        }
    };

    const handlePatientChange = (field, value) => {
        setPatientData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const handleAppointmentChange = (field, value) => {
        setAppointmentData(prev => ({
            ...prev,
            [field]: value
        }));
        setError('');
        setMessage('');
    };

    const handleConsentChange = (value) => {
        setMarketingConsent(value);
        setError('');
        setMessage('');
    };

    const validatePatientInfo = () => {
        if (!patientData.nume.trim()) {
            setError('Numele este obligatoriu');
            return false;
        }
        if (!patientData.prenume.trim()) {
            setError('Prenumele este obligatoriu');
            return false;
        }
        if (!patientData.email.trim()) {
            setError('Email-ul este obligatoriu');
            return false;
        }
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(patientData.email.trim())) {
            setError('Email-ul trebuie să fie într-un format valid (ex: nume@domain.com)');
            return false;
        }
        if (!patientData.telefon.trim()) {
            setError('Numărul de telefon este obligatoriu');
            return false;
        }
        // Validate phone number format (exactly 10 digits)
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(patientData.telefon.trim())) {
            setError('Numărul de telefon trebuie să conțină exact 10 cifre');
            return false;
        }

        return true;
    };

    const validateAppointmentDetails = () => {
        if (!appointmentData.id_specialitate) {
            setError('Specialitatea este obligatorie');
            return false;
        }
        if (!appointmentData.id_medic) {
            setError('Medicul este obligatoriu');
            return false;
        }
        if (!appointmentData.data) {
            setError('Data este obligatorie');
            return false;
        }
        if (!appointmentData.ora) {
            setError('Ora este obligatorie');
            return false;
        }
        return true;
    };

    const validateAllData = () => {
        if (!validatePatientInfo()) {
            return false;
        }
        if (!validateAppointmentDetails()) {
            return false;
        }
        if (!marketingConsent) {
            setError('Trebuie să vă dați acordul pentru a fi contactat în scopul confirmării programării.');
            return false;
        }
        return true;
    };

    const createAppointment = async () => {
        if (!validateAllData()) {
            return;
        }

        try {
            setLoading(true);

            // Step 1: Create patient
            const patientPayload = {
                ...patientData,
                nationalitate: 'romania',
                // Marketing consent for all channels (Email, SMS, Phone)
                marketing_telefonic: marketingConsent ? 1 : 0,
                marketing_sms: marketingConsent ? 1 : 0,
                marketing_email: marketingConsent ? 1 : 0
            };

            const patientResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/pacienti`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(patientPayload)
            });

            if (!patientResponse.ok) {
                throw new Error('Eroare la crearea pacientului');
            }

            const patientDataResult = await patientResponse.json();

            if (!patientDataResult.ok || !patientDataResult.item) {
                throw new Error(patientDataResult.error || 'Eroare la crearea pacientului');
            }

            const patientId = patientDataResult.item.id;

            // Step 2: Create appointment
            const appointmentPayload = {
                id_medic: parseInt(appointmentData.id_medic),
                id_pacient: parseInt(patientId),
                data: appointmentData.data,
                ora: appointmentData.ora,
                durata: 30, // Default duration
                observatii: appointmentData.observatii || ''
            };

            const appointmentResponse = await fetch(`${process.env.REACT_APP_API_URL}/v1/public/programari/insert`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${process.env.REACT_APP_API_TOKEN}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(appointmentPayload)
            });

            if (!appointmentResponse.ok) {
                const errorText = await appointmentResponse.text();
                throw new Error(`Eroare la crearea programării: ${errorText}`);
            }

            const appointmentResult = await appointmentResponse.json();

            if (appointmentResult.ok) {
                const selectedSpecialty = specialties.find((item) => String(item.id) === String(appointmentData.id_specialitate));
                const selectedDoctor = doctors.find((item) => String(item.id) === String(appointmentData.id_medic));

                const summary = {
                    specialitate: selectedSpecialty?.denumire || selectedSpecialty?.nume || 'Consultatie medicala',
                    data: appointmentData.data,
                    ora: appointmentData.ora,
                    medic: selectedDoctor ? `${selectedDoctor.nume} ${selectedDoctor.prenume}` : '',
                    email: patientData.email,
                    telefon: patientData.telefon,
                    nume: patientData.nume,
                    prenume: patientData.prenume
                };

                sessionStorage.setItem('appointmentSummary', JSON.stringify(summary));
                setMessage('Programarea a fost creată cu succes! Veți primi o confirmare în curând.');
                navigate('/multumim', { state: { summary } });
                // Reset form
                setPatientData({
                    nume: '',
                    prenume: '',
                    email: '',
                    telefon: ''
                });
                setAppointmentData({
                    id_specialitate: '',
                    id_medic: '',
                    data: '',
                    ora: '',
                    observatii: ''
                });
            } else {
                setError(appointmentResult.error || 'Eroare la crearea programării');
            }

        } catch (err) {
            console.error('Error creating appointment:', err);
            setError(err.message || 'Eroare la crearea programării');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ padding: '2rem', maxWidth: '600px', margin: '0 auto', fontFamily: 'Arial, sans-serif' }}>
            <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#006666', fontSize: '2rem' }}>
                Programare Online
            </h1>

            <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#666', fontSize: '1.1rem' }}>
                Programați-vă ușor și rapid la clinica noastră medicală
            </p>



            {error && (
                <div style={{
                    background: '#ffebee',
                    color: '#c62828',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #ffcdd2',
                    fontSize: '0.9rem'
                }}>
                    {error}
                </div>
            )}

            {message && (
                <div style={{
                    background: '#e8f5e8',
                    color: '#2e7d32',
                    padding: '1rem',
                    borderRadius: '8px',
                    marginBottom: '1rem',
                    border: '1px solid #c8e6c9',
                    fontSize: '0.9rem'
                }}>
                    {message}
                </div>
            )}

            <div style={{
                background: 'white',
                padding: '1rem',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
                maxWidth: '700px'
            }}>

                {/* Ultra-Compact Single Column Layout */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Combined Patient and Appointment Form */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>

                        {/* Patient Information */}
                        <div>
                            <h3 style={{ marginBottom: '0.75rem', color: '#333', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                Date personale
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Nume *
                                        </label>
                                        <input
                                            type="text"
                                            value={patientData.nume}
                                            onChange={(e) => handlePatientChange('nume', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            placeholder="Nume"
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                            Prenume *
                                        </label>
                                        <input
                                            type="text"
                                            value={patientData.prenume}
                                            onChange={(e) => handlePatientChange('prenume', e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.375rem',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem',
                                                height: '32px'
                                            }}
                                            placeholder="Prenume"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        value={patientData.email}
                                        onChange={(e) => handlePatientChange('email', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            height: '32px'
                                        }}
                                        placeholder="email@example.com"
                                    />
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Telefon *
                                    </label>
                                    <input
                                        type="tel"
                                        value={patientData.telefon}
                                        onChange={(e) => handlePatientChange('telefon', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            height: '32px'
                                        }}
                                        placeholder="07xxxxxxxx"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Appointment Details */}
                        <div>
                            <h3 style={{ marginBottom: '0.75rem', color: '#333', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                Detalii programare
                            </h3>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Specialitate *
                                    </label>
                                    <select
                                        value={appointmentData.id_specialitate}
                                        onChange={(e) => handleAppointmentChange('id_specialitate', e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            height: '32px'
                                        }}
                                        disabled={loading}
                                    >
                                        <option value="">Selectați specialitatea</option>
                                        {specialties.map(specialty => (
                                            <option key={specialty.id} value={specialty.id}>
                                                {specialty.denumire}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {appointmentData.id_specialitate && (
                                    <>
                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                                Medic *
                                            </label>
                                            <select
                                                value={appointmentData.id_medic}
                                                onChange={(e) => handleAppointmentChange('id_medic', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.375rem',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem',
                                                    height: '32px'
                                                }}
                                                disabled={loading}
                                            >
                                                <option value="">Selectați medicul</option>
                                                {doctors.map(doctor => (
                                                    <option key={doctor.id} value={doctor.id}>
                                                        {doctor.nume} {doctor.prenume}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                                Data *
                                            </label>
                                            <select
                                                value={appointmentData.data}
                                                onChange={(e) => handleAppointmentChange('data', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.375rem',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem',
                                                    height: '32px'
                                                }}
                                                disabled={loading || !appointmentData.id_medic || availableDates.length === 0}
                                            >
                                                <option value="">
                                                    {appointmentData.id_medic && availableDates.length === 0 && slotsLoaded
                                                        ? "Nu există date disponibile"
                                                        : appointmentData.id_medic
                                                            ? "Selectați data"
                                                            : "Selectați mai întâi medicul"
                                                    }
                                                </option>
                                                {availableDates.map(date => (
                                                    <option key={date.value} value={date.value}>
                                                        {date.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div>
                                            <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                                Ora *
                                            </label>
                                            <select
                                                value={appointmentData.ora}
                                                onChange={(e) => handleAppointmentChange('ora', e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.375rem',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    fontSize: '0.85rem',
                                                    height: '32px'
                                                }}
                                                disabled={loading || !appointmentData.data || availableTimes.length === 0}
                                            >
                                                <option value="">
                                                    {appointmentData.data && availableTimes.length === 0
                                                        ? "Nu există ore disponibile"
                                                        : appointmentData.data
                                                            ? "Selectați ora"
                                                            : "Selectați mai întâi data"
                                                    }
                                                </option>
                                                {availableTimes.map(time => (
                                                    <option key={time.value} value={time.value}>
                                                        {time.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {appointmentData.id_medic && slotsLoaded && availableDates.length === 0 && (
                                            <div style={{ padding: '1rem', background: '#fff3cd', border: '1px solid #ffeaa7', borderRadius: '4px', fontSize: '0.85rem', color: '#856404' }}>
                                                Nu există date disponibile pentru medicul selectat în următoarea perioadă.
                                            </div>
                                        )}

                                        {appointmentData.id_medic && slotsLoading && (
                                            <div style={{ padding: '1rem', background: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: '4px', fontSize: '0.85rem', color: '#1976d2' }}>
                                                Se încarcă programul medicului...
                                            </div>
                                        )}
                                    </>
                                )}

                                <div>
                                    <label style={{ display: 'block', marginBottom: '0.125rem', fontWeight: '600', color: '#333', fontSize: '0.85rem', textAlign: 'left' }}>
                                        Observații (opțional)
                                    </label>
                                    <textarea
                                        value={appointmentData.observatii}
                                        onChange={(e) => handleAppointmentChange('observatii', e.target.value)}
                                        placeholder="Menționați orice detalii..."
                                        style={{
                                            width: '100%',
                                            padding: '0.375rem',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            fontSize: '0.85rem',
                                            minHeight: '40px',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>

                                <div style={{ marginTop: '0.75rem', textAlign: 'left' }}>
                                    <label style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', fontSize: '0.9rem', color: '#333', lineHeight: '1.35', textAlign: 'left' }}>
                                        <input
                                            type="checkbox"
                                            checked={marketingConsent}
                                            onChange={(e) => handleConsentChange(e.target.checked)}
                                            style={{ marginTop: '0.15rem', width: '18px', height: '18px' }}
                                        />
                                        <span style={{ display: 'block', textAlign: 'left' }}>
                                            Îmi dau acordul pentru a fi contactat în scopurile confirmării programării și de marketing (Prin email, SMS și/sau Telefon). Mai multe detalii{' '}
                                            <a
                                                href="/politica-de-confidentialitate"
                                                style={{ color: '#006666', textDecoration: 'underline', fontWeight: '600' }}
                                            >
                                                aici
                                            </a>
                                            .
                                        </span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                        <button
                            onClick={createAppointment}
                            disabled={loading}
                            style={{
                                padding: '0.625rem 2rem',
                                background: loading ? '#ccc' : '#006666',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '0.95rem',
                                fontWeight: 'bold',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                minWidth: '180px'
                            }}
                        >
                            {loading ? 'Se programează...' : 'Programează-mă'}
                        </button>
                    </div>
                </div>

            </div>

            <div style={{ textAlign: 'center', marginTop: '2rem', color: '#666', fontSize: '0.9rem' }}>
                <p>* Câmpuri obligatorii</p>
                <p>Vă vom contacta pentru confirmarea programării în maximum 24 de ore.</p>
            </div>
        </div>
    );
}

export default ProgramariNew;
